/**
 * GCSelector - Shared component for selecting General Contractors
 * Supports multi-select (bidding) and single display (contracted) modes
 */
import { useState } from "react";
import { Check, X, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { sortAlphabetically } from "../../lib/sortUtils";

const GCSelector = ({
  generalContractors = [],
  selectedGcIds = [],
  onSelectionChange,
  contractedGcId,
  disabled = false,
  label = "General Contractors",
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const sortedGCs = sortAlphabetically(generalContractors);
  const filteredGCs = sortedGCs.filter(
    (gc) => gc.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedGCs = generalContractors.filter((gc) =>
    selectedGcIds.includes(gc.id)
  );

  const handleToggle = (gcId) => {
    if (disabled) return;
    const updated = selectedGcIds.includes(gcId)
      ? selectedGcIds.filter((id) => id !== gcId)
      : [...selectedGcIds, gcId];
    onSelectionChange(updated);
  };

  const handleRemove = (gcId) => {
    if (disabled) return;
    onSelectionChange(selectedGcIds.filter((id) => id !== gcId));
  };

  // Contracted mode - show single GC read-only
  if (contractedGcId) {
    const gc = generalContractors.find((g) => g.id === contractedGcId);
    return (
      <div className="form-group">
        <Label>{label}</Label>
        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Contracted
          </Badge>
          <span className="text-sm font-medium">{gc?.name || "Unknown GC"}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="form-group">
      <Label>{label}</Label>

      {/* Selected GCs as tags */}
      <div
        className="min-h-[40px] border rounded-md p-2 cursor-pointer hover:border-primary/50 transition-colors"
        onClick={() => !disabled && setPickerOpen(true)}
        data-testid="gc-selector-trigger"
      >
        {selectedGCs.length === 0 ? (
          <span className="text-sm text-muted-foreground">
            Click to select General Contractors...
          </span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selectedGCs.map((gc) => (
              <Badge
                key={gc.id}
                variant="secondary"
                className="flex items-center gap-1 pr-1"
              >
                {gc.name}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(gc.id);
                  }}
                  className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5"
                  data-testid={`gc-remove-${gc.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Picker Dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select General Contractors</DialogTitle>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search GCs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="gc-search-input"
            />
          </div>

          <div
            className="max-h-[50vh] overflow-y-auto space-y-1"
            style={{ overscrollBehavior: "contain" }}
          >
            {filteredGCs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No contractors found
              </p>
            ) : (
              filteredGCs.map((gc) => {
                const isSelected = selectedGcIds.includes(gc.id);
                return (
                  <div
                    key={gc.id}
                    className={`flex items-center gap-3 p-2.5 rounded-md cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-primary/5 border border-primary/30"
                        : "hover:bg-muted/50 border border-transparent"
                    }`}
                    onClick={() => handleToggle(gc.id)}
                    data-testid={`gc-option-${gc.id}`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(gc.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{gc.name}</p>
                      {(gc.city || gc.state) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {[gc.city, gc.state].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-primary shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-muted-foreground">
                {selectedGcIds.length} selected
              </span>
              <Button onClick={() => setPickerOpen(false)} data-testid="gc-picker-done">
                Done
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GCSelector;
